import { Buffer } from "buffer";
import { fromUnixTime } from "date-fns";
import { t } from "i18next";
import { API_REQUESTS_CACHE_INTERVAL_IN_SECONDS, APPLICANTS_CACHE_INTERVAL, CACHED_API_REQUESTS_KEY, CACHED_APPLICANTS_KEY, LOOKUPS_VERSION_NUMBER, PARTNERS_ALLOWED_UNLOCKS_TRIAL, RIYAL_URL, VAT_PERCENTAGE_IN_SAUDI } from "../constants/constants";
import { countries } from "../constants/countries";
import { careerWebsiteIcon, greenThumbsUp, iconChatMessages, iconCheckGreen, iconCloseFilled, iconHelp, sourceBaytIcon, sourceFacebookIcon, sourceGlassdoorIcon, sourceGoogleIcon, sourceGulfTalentIcon, sourceIndeedIcon, sourceInstagramIcon, sourceLinkedinIcon, sourceMonsterGulfIcon, sourceNaukriGulfIcon, sourceSnapchatIcon, sourceTelegramIcon, sourceTiktokIcon, sourceTwitterIcon, sourceWhatsappIcon, yelloMaybe, greyThumbsDown, interviewDeclinedIcon, iconFire, interviewFinishedIcon } from "../constants/icons";
import { ApplicantGenderEnum } from "../enums/ApplicantGender";
import { ChatApplicationUpdateType } from "../enums/ChatApplicationUpdateTypeEnum";
import { ChatMessageType } from "../enums/ChatMessageTypeEnum";
import { FileTypeEnum } from "../enums/FileTypeEnum";
import { GenderEnum, SaudiNationalityEnum } from "../enums/Gender";
import { LookupsSearchIndexEnum } from "../enums/LookupsSearchIndexEnum";
import { PMAEventEnum } from "../enums/PMAEventEnum";
import { PartnerInvitationResponseType } from "../enums/PartnerInvitationResponseTypeEnum";
import { PartnerStatus } from "../enums/PartnerStatus";
import { SubscriptionBillingCycleEnum, SubscriptionPlanCategoryEnum, SubscriptionStatusEnum } from "../enums/PartnerSubscription";
import { WorkerExperienceDurationEnum } from "../enums/WorkerExperienceDuration";
import { AiInterviewDecisionEnum, AiInterviewStatusEnum, ApplicantListItem, ApplicantsDragAndDropColumnIdEnum, OfflineApplicationSourceEnum } from "../interfaces/applicants/ApplicantListItem";
import { CachedApplicantItem } from "../interfaces/applicants/CachedApplicantItem";
import { ChatMessageApplicantDetails, LatestMsg } from "../interfaces/chat/ChatListItem";
import { PMAEventArgsInterface } from "../interfaces/chat/PMAEventArgsInterface";
import { PaLookups } from "../interfaces/partner/PaLookups";
import { PartnerIntercomData } from "../interfaces/partner/PartnerIntercomData";
import { Partner, PartnerUser } from "../interfaces/partner/PartnerProfile";
import { fromUnixTimestamp, getDurationBetweenDates } from "./date-helper";
import { SubscriptionPlanInterface } from "../interfaces/payment/SubscrptionPlanInterface";
import { FrontendPaymentSource } from "../enums/FrontendPaymentSource";
import { InvitationStringType, RoleEnum } from "../enums/EnterpriseEnums";
import { CompanySizeEnum } from "../enums/CompanySizeEnum";
import { ChatSenderType } from "../enums/ChatSenderTypeEnum";
import { FullTimeJob } from "../interfaces/job/Job";
import { SalaryFrequencyEnum } from "../enums/SalaryFrequencyEnum";
import { ContractTypeV2 } from "../enums/ContractTypeV2";
import { ApplicantsFilter } from "../interfaces/applicants/ApplicantsFilter";
import { ApplicantsFiltersEnumObject, LocalFilterEnum } from "../interfaces/vetting/VettingStoreInferface";
import { ApplicantRelevancyEnum, ApplicationStatusEnum } from "../enums/ApplicationStatus";
import { EducationLevel, LanguageProficiencyEnum, NationalityCategory } from "../interfaces/cv-search/CvSearchProfileInterface";
import { JobPostWorkplaceTypeEnum } from "../enums/JobPostWorkplaceTypeEnum";
import { FullTimeJobDetails } from "../interfaces/job/JobDetails";
import { ConversationListItemDto, ConversationStatus, ConversationTask, WhatsappSendMessageState } from "../interfaces/whatsapp/WhatsappInboxTypes";
import { displayPrice, getVolumeDiscountPercent, LOYAL_BASIC_DISCOUNT_PER_JOB } from "src/components/subscription/pricing-v2/pricingV2Utils";
import { trackSupportChatInitiated } from "../google-analytics/marketing";
import { getPageSearchParams, hasUtmSignal } from "../utm/classify-utm";
import { getUtmDataFromCookies as readUtmDataFromCookies } from "../utm/cookies";

const arabicNumbersMap = {
    '٠': '0', '۱': '1', '١': '1',
    '۲': '2', '٢': '2',
    '٣': '3', '٤': '4', '٥': '5', '٦': '6',
    '٧': '7', '٨': '8',
    '۹': '9', '٩': '9'
};

export const defaultUserAvatar = (gender: GenderEnum, style = 'circle') => {
    if (gender && gender.toUpperCase() === GenderEnum.FEMALE) {
        return style === 'circle' ? require("@src/assets/images/avatars/female-circle.svg").default : require("@src/assets/images/avatars/female-default-avatar.svg").default;
    }
    return style === 'circle' ? require("@src/assets/images/avatars/male-circle.svg").default : require("@src/assets/images/avatars/male-default-avatar.svg").default;
}

export const getApplicantGender = (gender: string) => {
    return gender
        ? gender === ApplicantGenderEnum.male ||
            gender?.toLowerCase() === GenderEnum.MALE.toLowerCase()
            ? GenderEnum.MALE
            : GenderEnum.FEMALE
        : GenderEnum.MALE;
}

export const setCookie = (cname: string, cvalue: any, days: number) => {
    const d = new Date();
    d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${d.toUTCString()}`;
    document.cookie = `${cname}=${encodeURIComponent(cvalue)};${expires}`;
}

export const getCookie = (cname: string) => {
    const name = `${cname}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        const c = ca[i].trim();
        if (c.indexOf(name) === 0) {
            const raw = c.substring(name.length, c.length);
            try { return decodeURIComponent(raw); } catch { return raw; }
        }
    }
    return "";
}

export const extractDigit = (string: any) => string.match(/\d+/)[0];

export const convertArabicNumbersToEnglish = (value: any) => {
    value = value.replace(/[٠۱١۲٢٣٤٥٦٧٨۹٩]/g, (match: any) => {
        //@ts-ignore
        return arabicNumbersMap[match];
    });
    return value;
}

export const isArabic = (str: any) => {
    const pattern = /[\u0600-\u06FF\u0750-\u077F]/;
    return pattern.test(str);
}

export const isEnglish = (str: any) => {
    const pattern = /[A-Za-z]/;
    return pattern.test(str);
}

export const isProduction = () => process.env.REACT_APP_ENV === 'production';
export const LOOKUPS_URL = () => process.env.REACT_APP_ENV === 'production' ? `https://cdn.sabbar.com/static-files/lookups.json?v=${LOOKUPS_VERSION_NUMBER}` : `https://cdn.giglaya.com/static-files/lookups.json?v=${LOOKUPS_VERSION_NUMBER}`;

export const isNotDemoPost = (status: string) => status !== "DEMO_POST";
export const isNotDraftPost = (status: string) => status !== "DRAFT";

export const getStatusColor = (status: string, isLightBG: boolean = true) => {

    const prefixKey = isLightBG ? 'light-' : '';

    const statusObj = {
        OPEN: `${prefixKey}success`,
        ACTIVE: `${prefixKey}success`,
        APPROVED: `${prefixKey}success`,
        UNDER_REVIEW: `${prefixKey}warning`,
        CLOSED: `${prefixKey}secondary`,
        REJECTED: `${prefixKey}danger`,
        DEMO_POST: `${prefixKey}info-2`,
        NOT_PROVIDED: `${prefixKey}info-2`,
        PENDING_APPROVAL: `${prefixKey}info-2`,
        EXPIRED: `${prefixKey}danger`,
        PENDING_REVIEW: `${prefixKey}warning`,
        PENDING: `${prefixKey}warning`,
    }

    return statusObj[status];
}


export const dataUrlToFile = (url: string, fileName: string) => {
    const [mediaType, data] = url.split(",");
    const mime = mediaType.match(/:(.*?);/)?.[0];
    let n = data.length;
    const arr = new Uint8Array(n);
    while (n--) {
        arr[n] = data.charCodeAt(n);
    }
    return new File([arr], fileName, { type: mime });
};

export const dataURItoBlob = (dataURI: any) => {
    const byteString = Buffer.from(dataURI.split(',')[1], 'base64');
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    const ia = new Uint8Array(byteString.length);

    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString[i];
    }

    return new Blob([ia], { type: mimeString });
}

export const toCapitalize = (string: string) => {
    if (!string) return;
    const arr = string.toLowerCase().split(' ');
    for (let i = 0; i < arr.length; i++) {
        arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
    }
    const string2 = arr.join(' ');
    return string2;
}

export const enumToArray = (item, prefixKey = "enum_") => {
    const list = Object.keys(item);
    const options = list.map((type) => {
        return { value: item[type], label: t(`${prefixKey}${type}`) };
    });
    return options;
}

export const enumToArrayWithDescription = (item, prefixKey = "enum_") => {
    const list = Object.keys(item);
    const options = list.map((type) => {
        return { value: item[type], label: t(`${prefixKey}${type}`), description: t(`${prefixKey}${type}_description`) };
    });
    return options;
}

export const GetCenterPoint = (latLng: any) => {
    if (latLng === undefined) return;
    const center = { lat: latLng.lat(), long: latLng.lng() };
    const centerPoint = [parseFloat(center.lat.toFixed(6)), parseFloat(center.long.toFixed(6))];
    return { centerPoint, center }
}

export const convertArrayToObjectKeyValue = (items: any[]) => {
    const objectKeyValuePair = items.reduce(function (result, item) {
        var key = Object.keys(item)[0];
        result[key] = item[key];
        return result;
    }, {});
    return objectKeyValuePair;
}

export const getAge = (dob: number) => {
    const dateOfBirth = fromUnixTime(dob);
    const month_diff = Date.now() - dateOfBirth.getTime();
    const age_dt = new Date(month_diff);
    var year = age_dt.getUTCFullYear();
    var age = Math.abs(year - 1970);
    return age;
}

export const querySearchParams = (items) => {
    let query = new URLSearchParams();

    Object.entries(items).forEach(([k, v]) => {
        if (Array.isArray(v)) {
            v.forEach((vv) => {
                query.append(`${k}[]`, vv);
            });
        } else {
            //@ts-ignore
            query.append(k, v);
        }
    });
    return query;
};

export const getExperienceDurationMapping = () => {
    return [
        {
            value: WorkerExperienceDurationEnum.LESS_3_MONTHS,
            label: t(`enum_${WorkerExperienceDurationEnum.LESS_3_MONTHS}`)
        },
        {
            value: WorkerExperienceDurationEnum.LESS_6_MONTHS,
            label: t(`enum_${WorkerExperienceDurationEnum.LESS_6_MONTHS}`)
        },
        {
            value: WorkerExperienceDurationEnum.LESS_1_YEAR,
            label: t(`enum_${WorkerExperienceDurationEnum.LESS_1_YEAR}`)
        },
        {
            value: WorkerExperienceDurationEnum.LESS_2_YEARS,
            label: t(`enum_${WorkerExperienceDurationEnum.LESS_2_YEARS}`)
        },
        {
            value: WorkerExperienceDurationEnum.MORE_2_YEARS,
            label: t(`enum_${WorkerExperienceDurationEnum.MORE_2_YEARS}`)
        }
    ]
}

export const getAddressFromCoordinates = async (lat: number, lng: number): Promise<{ locationEn, locationAr }> => {
    if (lat && lng) {
        const geocoder = new google.maps.Geocoder();
        const latlng = new google.maps.LatLng(lat, lng);

        const [locationEn, locationAr] = await Promise.all([
            geocoder.geocode({ location: latlng, language: 'en' }, (results, status) => {
                if (results?.length > 0 && status === google.maps.GeocoderStatus.OK) {
                    const address = results[0].formatted_address;
                    const country = results[0].address_components.find(x => x.types.find(y => y === 'country'))?.short_name;
                    const city = results[0].address_components.find(x => x.types.find(y => y === 'locality'))?.long_name;
                    const district = results[0].address_components.find(x => x.types.find(y => y === 'sublocality'))?.long_name;
                    const placeId = results[0].place_id;
                    const location = { country, address, city, district, location: latlng, placeId };
                    return location;
                }
            }),
            geocoder.geocode({ location: latlng, language: 'ar' }, (results, status) => {
                if (results?.length > 0 && status === google.maps.GeocoderStatus.OK) {
                    const address = results[0].formatted_address;
                    const country = results[0].address_components.find(x => x.types.find(y => y === 'country'))?.short_name;
                    const city = results[0].address_components.find(x => x.types.find(y => y === 'locality'))?.long_name;
                    const district = results[0].address_components.find(x => x.types.find(y => y === 'sublocality'))?.long_name;
                    const placeId = results[0].place_id;
                    const location = { country, address, city, district, location: latlng, placeId };
                    return location;
                }
            })
        ]);
        return { locationEn, locationAr }
    }
};

export const getMatchingAddressBetweenGoogleAndUser = (addresses: any, selectedAddress: any) => {
    if (addresses?.locationEn?.results?.length > 0) {
        const enAddresses = addresses?.locationEn?.results;
        const arAddresses = addresses?.locationAr?.results;
        if (enAddresses?.length > 0 && arAddresses?.length > 0) {
            let matchSelectedAddress = { en: enAddresses[0], ar: arAddresses[0] };
            const matchEnSelectedAddressIndex = enAddresses.findIndex(res => res?.formatted_address === selectedAddress);
            const matchArSelectedAddressIndex = arAddresses.findIndex(res => res?.formatted_address === selectedAddress);
            if (matchEnSelectedAddressIndex && matchEnSelectedAddressIndex !== -1) {
                matchSelectedAddress = { en: enAddresses[matchEnSelectedAddressIndex], ar: arAddresses[matchEnSelectedAddressIndex] };
            } else if (matchArSelectedAddressIndex && matchArSelectedAddressIndex !== -1) {
                matchSelectedAddress = { en: enAddresses[matchArSelectedAddressIndex], ar: arAddresses[matchArSelectedAddressIndex] };
            }
            return { en: matchSelectedAddress?.en?.formatted_address, ar: matchSelectedAddress?.ar?.formatted_address };
        }
    }
    return {};
}

export const getGoogleCityFromLocation = (addresses: any) => {
    if (addresses?.locationEn?.results?.length > 0) {
        const enAddresses = addresses?.locationEn?.results;
        const arAddresses = addresses?.locationAr?.results;
        if (enAddresses?.length > 0 && arAddresses?.length > 0) {
            const cityEn = enAddresses[0]?.address_components?.find(x => x.types.find(y => y === 'locality'))?.long_name;
            const cityAr = arAddresses[0]?.address_components?.find(x => x.types.find(y => y === 'locality'))?.long_name;
            return { en: cityEn, ar: cityAr };
        }
    }
    return {};
}

export const getLocationDisplayName = (locationInfo: any, lang: any) => {
    return locationInfo?.localizedAddress?.[lang] || locationInfo?.address || locationInfo?.city || null;
}

export const logoutUser = () => {
    localStorage.clear();
    window.location.href = '/#/login';
}

export const escapeHTML = (text: string) => {
    return text ? text.replace(/(<([^>]+)>)/ig, '') : ""
}

export const getLookupLocationDb = (): LookupsSearchIndexEnum => {
    return process.env.REACT_APP_ENV === 'production' ? LookupsSearchIndexEnum.PROD_GEO : LookupsSearchIndexEnum.PROD_GEO// LookupsSearchIndexEnum.STG_GEO; // currently stg_geo is disabled for typesense
}

export const sortArrayByLabel = (items: any, key: string) => {
    return items.sort((row1, row2) => {
        let x = row1[key]?.toLowerCase();
        let y = row2[key]?.toLowerCase();
        if (x < y) { return -1; }
        if (x > y) { return 1; }
        return 0;
    });
}

export const sortObjectKeys = (objToSort: any) => {
    let obj = {};
    Object.keys(objToSort)
        .sort()
        .forEach(function (v, i) {
            obj[v] = objToSort[v];
        });
    return obj;
}

const cyrb53 = (str, seed = 0) => {
    let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
    for (let i = 0, ch; i < str.length; i++) {
        ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};


export const getCacheKeyForObject = (obj: any, entityNamePrefix?: string) => {
    let cacheKey = entityNamePrefix ? `${entityNamePrefix}-` : 'ck-'; //ck for cache key
    if (Object.keys(obj).length) {
        const sortedString = JSON.stringify(sortObjectKeys(obj));
        const hashedKey = cyrb53(sortedString)
        cacheKey += `${hashedKey}`;
    } else {
        return '';
    }
    return cacheKey;
}

export const clearApplicantsCacheKeys = (filterKey: 'timestamp' | 'jobId', jobId?: string) => {
    let applicantsFromLSCache: CachedApplicantItem[] = localStorage.getItem(CACHED_APPLICANTS_KEY) ? JSON.parse(localStorage.getItem(CACHED_APPLICANTS_KEY)) : [];
    if (applicantsFromLSCache.length === 0) return;

    let newApplicantsFromLSCache = [...applicantsFromLSCache];
    const currentTimestamp = Math.ceil(Date.now() / 1000);

    if (filterKey === 'timestamp') {
        newApplicantsFromLSCache = newApplicantsFromLSCache.filter(ap => {
            if (currentTimestamp - ap.timestamp > APPLICANTS_CACHE_INTERVAL) {
                return false;
            }
            return true;
        });
    } else if (filterKey === 'jobId' && jobId) {
        newApplicantsFromLSCache = newApplicantsFromLSCache.filter(ap => ap.jobId !== jobId);
    }

    localStorage.setItem(CACHED_APPLICANTS_KEY, JSON.stringify(newApplicantsFromLSCache));
}

export interface ApiCacheItem {
    timestamp: number;
    cacheKey: number;
    data: any;
}

export const getApiResponseFromCache = (apiUrl: string, queryParams: any) => {
    let keyString = apiUrl + '?' + JSON.stringify(sortObjectKeys(queryParams));
    const cacheKey = cyrb53(keyString);
    const currentTimestamp = Math.ceil(Date.now() / 1000);

    let cacheData: ApiCacheItem[] = localStorage.getItem(CACHED_API_REQUESTS_KEY) ? JSON.parse(localStorage.getItem(CACHED_API_REQUESTS_KEY)) : [];
    if (cacheData.length === 0) return null;

    for (const data of cacheData) {
        if (data.cacheKey === cacheKey && currentTimestamp - data.timestamp <= API_REQUESTS_CACHE_INTERVAL_IN_SECONDS) { // keep cache for API_REQUESTS_CACHE_INTERVAL_IN_SECONDS 
            return data.data;
        } else if (data.cacheKey === cacheKey && currentTimestamp - data.timestamp > API_REQUESTS_CACHE_INTERVAL_IN_SECONDS) {
            // delete cache
            const newCacheData = cacheData.filter(c => c.cacheKey !== cacheKey);
            localStorage.setItem(CACHED_API_REQUESTS_KEY, JSON.stringify(newCacheData));
        }
    }

    return false;
}

export const clearAllApiResponsesFromCache = () => {
    localStorage.removeItem(CACHED_API_REQUESTS_KEY);
}

export const clearOldApiResponsesFromCache = () => {
    const currentTimestamp = Math.ceil(Date.now() / 1000);

    let cacheData: ApiCacheItem[] = localStorage.getItem(CACHED_API_REQUESTS_KEY) ? JSON.parse(localStorage.getItem(CACHED_API_REQUESTS_KEY)) : [];
    if (cacheData.length === 0) return null;

    let newCacheData = [...cacheData];

    for (const data of cacheData) {
        if (currentTimestamp - data.timestamp > API_REQUESTS_CACHE_INTERVAL_IN_SECONDS) {
            newCacheData = newCacheData.filter(c => c.cacheKey !== data.cacheKey);
        }
    }

    localStorage.setItem(CACHED_API_REQUESTS_KEY, JSON.stringify(newCacheData));
    return true;
}

export const setApiResponseInCache = (apiUrl: string, queryParams: any, data: any) => {
    let keyString = apiUrl + '?' + JSON.stringify(sortObjectKeys(queryParams));
    const cacheKey = cyrb53(keyString);
    const currentTimestamp = Math.ceil(Date.now() / 1000);
    let cacheData: ApiCacheItem[] = localStorage.getItem(CACHED_API_REQUESTS_KEY) ? JSON.parse(localStorage.getItem(CACHED_API_REQUESTS_KEY)) : [];
    cacheData.push({ cacheKey, timestamp: currentTimestamp, data });
    // This code ensures the API cache stored in localStorage does not grow indefinitely.
    // - If there are more than 20 cache items, it removes the 10 oldest items (from the front of the array).
    // - If there are more than 10 items (but 20 or fewer), it removes only the oldest item.
    if (cacheData.length > 20) {
        for (let i = 0; i < 10; i++) {
            cacheData.shift();
        }
    } else if (cacheData.length > 10) {
        cacheData.shift();
    }
    localStorage.setItem(CACHED_API_REQUESTS_KEY, JSON.stringify(cacheData));
}


export const invalidateApiResponseInCache = (apiUrl: string, queryParams: any) => {
    let keyString = apiUrl + '?' + JSON.stringify(sortObjectKeys(queryParams));
    const cacheKey = cyrb53(keyString);

    let cacheData: ApiCacheItem[] = localStorage.getItem(CACHED_API_REQUESTS_KEY) ? JSON.parse(localStorage.getItem(CACHED_API_REQUESTS_KEY)) : [];
    if (cacheData.length === 0) return null;

    const newCacheData = cacheData.filter(c => c.cacheKey !== cacheKey);
    localStorage.setItem(CACHED_API_REQUESTS_KEY, JSON.stringify(newCacheData));
}

export const invalidateJobPostCache = (jobId: string) => {
    let cacheData: ApiCacheItem[] = localStorage.getItem(CACHED_API_REQUESTS_KEY) ? JSON.parse(localStorage.getItem(CACHED_API_REQUESTS_KEY)) : [];
    if (cacheData.length === 0 || !jobId) return null;

    const newCacheData = cacheData.filter(c => c?.data?.jobId !== jobId || !c?.data?.jobId);
    localStorage.setItem(CACHED_API_REQUESTS_KEY, JSON.stringify(newCacheData));
}

export const invalidateJobPostCacheForJobsPreMh = () => {
    let cacheData: ApiCacheItem[] = localStorage.getItem(CACHED_API_REQUESTS_KEY) ? JSON.parse(localStorage.getItem(CACHED_API_REQUESTS_KEY)) : [];
    if (cacheData.length === 0) return null;
    const newCacheData = cacheData.filter(c => c?.data?.jobId);
    localStorage.setItem(CACHED_API_REQUESTS_KEY, JSON.stringify(newCacheData));
}

export const getApplicantNationality = (countryCode: string, gender: string, lang: string): string => {
    let nationality = '';
    const country = countries.find(c => c.code === countryCode);
    if (country) {
        if (lang === 'en') {
            nationality = country.nationalityEn;
        } else {
            if (gender === 'FEMALE') {
                nationality = country.nationalityArFemale;
            } else {
                nationality = country.nationalityAr;
            }
        }
    }

    return nationality;
}

export const handleScrollToTop = (top = 0) => {
    window.scroll({ top: top, behavior: 'smooth' })
}

export const addHotjarTrackingForSelect = () => {
    document.getElementById('react-select-2-input')?.classList?.add('data-hj-allow');
    document.getElementById('react-select-3-input')?.classList?.add('data-hj-allow');
    document.getElementById('react-select-4-input')?.classList?.add('data-hj-allow');
    document.getElementById('react-select-5-input')?.classList?.add('data-hj-allow');
    document.getElementById('react-select-6-input')?.classList?.add('data-hj-allow');
    document.getElementById('react-select-7-input')?.classList?.add('data-hj-allow');
    document.getElementById('react-select-8-input')?.classList?.add('data-hj-allow');
}

export const isSameObject = (obj1: any, obj2: any) => {

    const sortedObj1 = sortObjectKeys(obj1);
    const sortedObj2 = sortObjectKeys(obj2);

    return JSON.stringify(sortedObj1) === JSON.stringify(sortedObj2);
}

export const groupByData = (data: any, key: string, key2: string = "") => {
    const groups = data.reduce((items: any, value: any) => {
        const item = key2 ? value[key][key2] : value[key];
        if (!items[item]) {
            items[item] = [];
        }
        items[item].push(value);
        return items;
    }, {});

    const groupArrays = key2 ? Object.keys(groups).map((key2) => {
        return {
            label: key2,
            options: groups[key2]
        };
    }) : Object.keys(groups).map((key) => {
        return {
            label: key,
            options: groups[key]
        };
    });

    return groupArrays
}

export const subSectorsGroupedBySectorOld = (payload, key, lang) => {
    const result = payload.map(hit => {
        return {
            ...hit,
            displayName: hit?.highlight?.name?.[lang]?.value?.replace(/mark/g, 'em') || hit?.document?.name?.[lang],
            objectID: hit?.document?.objectID || hit?.document?.id,
            industryEnum: hit?.document?.industryEnum,
            industry: hit?.document?.industry,
        }
    });

    const group = groupByData(result, key);

    const formattedGroup = [];

    group.forEach(x => formattedGroup.push({
        ...x,
        label: x.options?.length > 0 ? x.options?.[0]?.industry?.[lang] : x.label,
    }));
    return formattedGroup
}

export const subSectorsGroupedBySector = (payload, key, lang) => {
    const result = payload.map(hit => {
        return {
            ...hit,
            displayName: hit?.highlight?.name?.[lang]?.value?.replace(/mark/g, 'em') || hit?.document?.name?.[lang],
            objectID: hit?.document?.objectID || hit?.document?.id,
            industryEnum: hit?.document?.industryEnum,
            industry: hit?.document?.industry,
            label: hit?.document?.industry?.[lang]
        }
    });

    return result;
}

export const groupedBySkills = (payload, key, lang) => {
    const result = payload.map(hit => {
        return {
            ...hit,
            label: hit?.highlight?.name?.[lang]?.value?.replace(/mark/g, 'em') || hit?.document?.name?.[lang],
            value: hit?.objectID
        }
    });

    const group = groupByData(result, key);

    return group
}

export const removeDuplicatesFromArray = (items: string[], key: string) => {
    return items.length > 0 ? Array.from(new Set(items.map(x => x[key]))) : []
}

export const removeDuplicatesFromArrayOfObjects = (items: any, key: string) => {
    //@ts-ignore
    return [...new Map(items.map(item => [item[key], item])).values()];
}

export const removeDuplicatesFromArrayOfObjects2 = (items: any, key: string) => {
    return items?.length > 0 ? items?.filter(
        (item, index, self) =>
            index === self.findIndex(other => other[key] === item[key])
    ) : [];
}

export const preparePartnerHotjarObject = (partner: Partner, partnerUser: PartnerUser) => {
    const name = partnerUser?.name;
    const firstName = name?.split(' ')[0];
    const lastName = name?.substring(firstName?.length)?.trim();

    return {
        'partnerUserId': partnerUser?.id?.split('-')?.[0],
        'partnerId': partner.reference,
        'brandName': partner.name,
        'firstName': firstName,
        'lastName': lastName,
        'mobile': partnerUser?.mobile,
        'email': partnerUser?.email,
        'leadSource': partner.leadSource,
        'industry': partner.industriesAndSubIndustriesEn,
        'businessSize': partner.employeeCount,
        'hqLocation': partner.location,
        'registeredAt': partner.registrationDate ? fromUnixTimestamp(partner.registrationDate, 'yyyy-MM-dd') : null,
        'isQualified': partner.isQualified,
        'isCompany': partner.status === PartnerStatus.APPROVED,
        'status': partner.status,
        'totalJobs': partner.jobPostsCount,
        'totalPaidJobs': partner.paidJobPostsCount,
        'subscriptionPlan': partner?.currentSubscription?.subscriptionCategory || 'FREE',
        'subscriptionDuration': partner?.currentSubscription?.billingCycle || null,
        'subscriptionStatus': partner?.currentSubscription?.subscriptionStatus || null,
        'subscriptionStartAt': partner?.currentSubscription?.subscriptionBillingCycleStartAt ? fromUnixTimestamp(partner?.currentSubscription?.subscriptionBillingCycleStartAt, 'yyyy-MM-dd') : null,
        'subscriptionEndAt': partner?.currentSubscription?.subscriptionBillingCycleEndAt ? fromUnixTimestamp(partner?.currentSubscription?.subscriptionBillingCycleEndAt, 'yyyy-MM-dd') : null,
        'totalSubscriptions': partner?.currentSubscription?.totalSubscriptionsCount || 0,
        'totalSales': partner?.currentSubscription?.totalSales || 0,
    }
}

export const preparePartnerIntercomObject = (partner: Partner, partnerUser: PartnerUser, lang: string): PartnerIntercomData => {
    return {
        api_base: "https://api-iam.intercom.io",
        app_id: process.env.REACT_APP_INTERCOM_APP_ID,
        language_override: lang,
        user_id: partnerUser.id,
        name: partnerUser.name,
        email: partnerUser.email,
        user_hash: partnerUser.intercomUserIdHash,
        created_at: partnerUser.createdAt,
        phone: partnerUser.mobile,
        company: {
            company_id: partner.partnerId,
            created_at: partner.registrationDate,
            name: partner.name,
            plan: partner?.currentSubscription?.subscriptionCategory || 'FREE',
            industry: partner.industriesAndSubIndustriesEn ? partner.industriesAndSubIndustriesEn : null,
            total_jobs: partner.jobPostsCount,
            business_size: partner.employeeCount,
            hq_location: partner?.location,
            lead_source: partner.leadSource,
            status: partner.status,
            subscription_plan: partner?.currentSubscription?.subscriptionCategory || 'FREE',
            subscription_duration: partner?.currentSubscription?.billingCycle || null,
            subscription_status: partner?.currentSubscription?.subscriptionStatus || null,
            subscription_start_at: partner?.currentSubscription?.subscriptionBillingCycleStartAt || null,
            subscription_end_at: partner?.currentSubscription?.subscriptionBillingCycleEndAt || null,
            total_subscriptions: partner?.currentSubscription?.totalSubscriptionsCount || 0,
            total_sales: partner?.currentSubscription?.totalSales || '0',
        },
    }
}

export const preparePartnerIntercomObjectBE = (partner: Partner, partnerUser: PartnerUser, lang: string): PartnerIntercomData => {
    return {
        api_base: "https://api-iam.intercom.io",
        app_id: process.env.REACT_APP_INTERCOM_APP_ID,
        language_override: lang,
        user_id: partnerUser.id,
        name: partnerUser.name,
        email: partnerUser.email,
        user_hash: partnerUser.intercomUserIdHash,
        created_at: partnerUser.createdAt
    }
}

export const preparePartnerIntercomObjectForVisitor = (): PartnerIntercomData => {
    return {
        api_base: "https://api-iam.intercom.io",
        app_id: process.env.REACT_APP_INTERCOM_APP_ID
    }
}

export const roundPrice = (price: number, decimals = 2) => {
    const factor = 10 ** decimals;
    return Math.round((price + Number.EPSILON) * factor) / factor;
}

export const getPlanPrice = (
    plan: SubscriptionPlanInterface,
    billingCycle: SubscriptionBillingCycleEnum,
    numberOfJobPostsTemp: number,
    opts?: { isLoyalCustomer?: boolean }
): { subtotal: number, vatAmount: number, total: number, original: number } => {
    let numberOfJobPosts = numberOfJobPostsTemp || 1;
    let price = billingCycle === SubscriptionBillingCycleEnum.YEARLY ? plan?.yearlyBillingPrice : plan?.monthlyBillingPrice || 0;
    const isDiscountActive = plan?.isDiscountActive && plan?.discount?.applicableBillingCycles.includes(billingCycle);

    const vatPercantage = VAT_PERCENTAGE_IN_SAUDI / 100;

    if (numberOfJobPosts > 1 && billingCycle === SubscriptionBillingCycleEnum.YEARLY && isDiscountActive) {
        price = plan.monthlyBillingPrice;
    }

    let subtotal = price * numberOfJobPosts;
    const isLoyalBasic = !!opts?.isLoyalCustomer && plan?.category === SubscriptionPlanCategoryEnum.CORE;
    const loyalSavings = isLoyalBasic ? LOYAL_BASIC_DISCOUNT_PER_JOB * numberOfJobPosts : 0;
    const original = price * numberOfJobPosts + loyalSavings;

    if (plan?.isDiscountActive && plan?.discount?.applicableBillingCycles.includes(billingCycle)) {
        subtotal = subtotal - (subtotal * (plan.discount.percentage / 100));
    } else if (numberOfJobPosts > 1) {
        // Same volume tiers as the pricing page: 5% at 2–3 jobs, 10% at 4–5.
        const bulkDiscountPercentage = getVolumeDiscountPercent(numberOfJobPosts);
        subtotal = subtotal - (subtotal * (bulkDiscountPercentage / 100));
    }

    subtotal = displayPrice(subtotal);
    const vatAmount = subtotal * vatPercantage;

    const prices = {
        original: displayPrice(original),
        subtotal: displayPrice(subtotal),
        vatAmount: displayPrice(vatAmount, true),
        total: displayPrice(subtotal + vatAmount, true),
    }

    return prices;
}

export const isLocationStateValid = (state: any) => {
    if (!state) return false;
    if (typeof state !== "object") return false;
    return true;
}

export const addAudioElement = (blob: Blob, container: HTMLCollection, classNames?: string) => {
    const url = URL.createObjectURL(blob);
    const audio = document.createElement('audio');
    audio.src = url;
    audio.controls = true;
    audio.className = classNames;
    container[0].appendChild(audio);
};

export const callPmaEvent = (pmaEvent: PMAEventEnum, args: PMAEventArgsInterface) => {
    if (!window.flutter_inappwebview) return;
    window.flutter_inappwebview.callHandler(pmaEvent, args);
}

export const getFileName = (path: string) => {
    // https://stackoverflow.com/a/25221100
    return path.replace(/^.*[\\/]/, '')
}

export const imageExtensions = ['png', 'jpg', 'jpeg'];
export const videoExtensions = ['mp4', 'mov'];
export const documentExtensions = ['pdf', 'doc', 'docx', 'xlsx', 'txt'];
export const voiceExtensions = ['mp3', 'opus', 'wav', 'm4a'];

export const getLatestChatMsgPreview = (latestMsg: LatestMsg, applicant: any): { msgType: number, msg: string, isOrange?: boolean } => {
    if (!latestMsg) return null;

    const now = Math.ceil(Date.now() / 1000);
    const diff = now - latestMsg.createdAt;
    const secondsInADay = 24 * 3600;

    if (latestMsg.senderType === ChatSenderType.WORKER && latestMsg.createdAt > (now - (2 * secondsInADay)) && latestMsg.createdAt <= (now - (1 * secondsInADay))) { // last message was 1 day ago 
        return { msgType: ChatMessageType.TEXT, msg: t(`received_reply_ago`, { value: Math.ceil(diff / 86400) }), isOrange: true };
    }

    if (latestMsg.senderType === ChatSenderType.PARTNER && latestMsg.createdAt < (now - (3 * secondsInADay))) { // last message was 3 days ago or more
        return { msgType: ChatMessageType.TEXT, msg: t(`no_response_since`, { value: Math.ceil(diff / 86400) }), isOrange: true };
    }

    if (latestMsg.msgType === ChatMessageType.TEXT) {
        return { msgType: latestMsg.msgType, msg: latestMsg.msg };
    }

    if (latestMsg.fileUrl) {
        const fileExtensionArray = latestMsg.fileUrl.split('.');
        const fileExtension = fileExtensionArray[fileExtensionArray.length - 1];
        if (imageExtensions.includes(fileExtension)) {
            return { msgType: 1, msg: FileTypeEnum.IMAGE as string }
        } else if (videoExtensions.includes(fileExtension)) {
            return { msgType: 1, msg: FileTypeEnum.VIDEO as string }
        } else if (documentExtensions.includes(fileExtension)) {
            return { msgType: 1, msg: FileTypeEnum.DOCUMENT as string }
        } else if (voiceExtensions.includes(fileExtension)) {
            return { msgType: 2, msg: FileTypeEnum.VOICE as string }
        }
    }
    if (latestMsg.msgType === ChatMessageType.APPLICATION_UPDATE) {
        return { msgType: latestMsg.msgType, msg: getChatApplicationUpdateTextAndIcon(latestMsg?.applicationDetails, applicant).title };
    }


    return { msgType: latestMsg.msgType, msg: latestMsg.msg };
}

export const getChatApplicationUpdateTextAndIcon = (applicationDetails: ChatMessageApplicantDetails, applicant: any, isChatBubble?: boolean) => {
    let title = '';
    let icon = null;
    let updateType = applicationDetails.updateType;

    if (updateType === ChatApplicationUpdateType.APPLIED && isChatBubble) {
        title = `<span class="font-bold text-dark">${applicant.firstName}</span> ${applicant.gender === 'FEMALE' ? t('applied_for_job_female') : t('applied_for_job')}`;
        icon = iconCheckGreen;
    } else if (updateType === ChatApplicationUpdateType.APPLIED) {
        title = `${applicant.firstName} ${applicant.gender === 'FEMALE' ? t('applied_for_job_female') : t('applied_for_job')}`;
        icon = iconCheckGreen;
    } else if (updateType === ChatApplicationUpdateType.VIEWED) {
        title = `${t('you_viewed_1')} ${applicant.firstName} ${t('you_viewed_2')}`;
        icon = iconCheckGreen;
    } else if (updateType === ChatApplicationUpdateType.NOT_SELECTED) {
        title = `${t('wasnt_selected_1')} ${applicant.firstName} ${t('wasnt_selected_2')}`
        icon = iconCloseFilled;
    } else if (updateType === ChatApplicationUpdateType.SHORT_LISTED) {
        title = `${t('you_shortlisted_1')} ${applicant.firstName} ${t('you_shortlisted_2')}`;
        icon = iconFire;
    } else if (updateType === ChatApplicationUpdateType.UNLOCKED) {
        title = t('partner_unlocked_applicant', { value: applicant.firstName });
        icon = iconFire
    } else if (updateType === ChatApplicationUpdateType.JOB_CLOSED) {
        title = t('job_status_closed_onhover')
    } else if (updateType === ChatApplicationUpdateType.INVITATION_SENT) {
        title = t(applicant.gender === 'FEMALE' ? 'invitation_received_female' : 'invitation_received', { value: applicant.firstName });
        icon = iconChatMessages;

    } else if (updateType === ChatApplicationUpdateType.INVITATION_RESPONSE) {
        if (applicationDetails.invitationResponse === PartnerInvitationResponseType.ACCEPTED) {
            title = t(applicant.gender === 'FEMALE' ? 'applicant_job_interested_female' : 'applicant_job_interested', { value: applicant.firstName });
            icon = iconCheckGreen;
        } else if (applicationDetails.invitationResponse === PartnerInvitationResponseType.NEED_MORE_INFO) {
            title = t(applicant.gender === 'FEMALE' ? 'applicant_job_need_more_details_female' : 'applicant_job_need_more_details', { value: applicant.firstName });
            icon = iconHelp;
        } else {
            const rejectionReason = t(`applicant_job_rejection_${applicationDetails.invitationRejectionReason}`);
            const rejectionReasonTitle = t('applicant_job_not_interested', { value: applicant.firstName });
            title = `${rejectionReasonTitle} "${rejectionReason}"`;
            icon = iconCloseFilled;
        }
    }

    return { title, icon };
}

export const generateChatDataForPma = (applicant: ApplicantListItem, rtmToken: string, chatId: string) => {
    return {
        chatId: chatId,
        worker: {
            id: applicant.externalId || applicant.id,
            firstName: applicant.firstName,
            lastName: applicant.lastName,
            mobile: applicant.mobile,
            email: applicant.email,
            age: applicant.age,
            nationality: applicant.nationality,
            bio: applicant.bio,
            city: applicant.city,
            address: applicant.district,
            profileImageUrl: applicant.profileImage || applicant.profileImageUrl,
            lastActiveAt: applicant.lastUpdateAt || applicant.lastActiveAt,
            gender: applicant.gender
        },
        latestMsg: applicant.lastMessage ? {
            id: applicant.lastMessage.id,
            msgType: applicant.lastMessage.msgType,
            senderType: applicant.lastMessage.senderType,
            msg: applicant.lastMessage.msg,
            fileUrl: applicant.lastMessage?.fileUrl,
            createdAt: applicant.lastMessage?.createdAt,
            seenByPartnerAt: applicant.lastMessage?.seenByPartnerAt,
            seenByWorkerAt: applicant.lastMessage?.seenByWorkerAt,
            applicationDetails: null
        } : null,
        newMessagesCount: applicant.newUpdatesCount || 0,
        rtmToken: rtmToken
    }
}


export const convertLookupResultsToDropdownOptions = (items: any, key: string, lang: string) => {
    const options = [];

    if (items.length > 0) {

        items.map(r => r && options.push({ value: r.objectID, label: r?.[key]?.[lang] }))

        const sortedOptions = sortArrayByLabel(options, 'label');

        return sortedOptions;
    }

    return options;
}

export const getNow = () => {
    return Math.ceil(Date.now() / 1000);
}

export const prepareChatMsgPayload = (id: number, text: string, chatId: string, msgType: number, fileUrl: string, senderType: number) => {
    return {
        id: id,
        msgType: msgType,
        senderType: senderType,
        msg: text,
        fileUrl: fileUrl,
        createdAt: getNow(),
        createdDate: fromUnixTimestamp(getNow(), 'yyyy-MM-dd'),
        chatId,
        seenByPartnerAt: getNow(),
        seenByWorkerAt: null,
        applicationDetails: null
    }
}

export const getLookupByKey = (lookups: any, key: keyof PaLookups, lang: string): { value: string, label: string, description?: string, isVisible?: boolean, isHeadquarter?: boolean, isSabbar?: boolean, isMinistry?: boolean }[] => {
    if (!lookups) return;
    return lookups[key]?.map(l => {
        // TODO: sometimes it throws an exception (create job form) id cannot read from null
        return l && {
            value: l.id,
            label: l.content[lang],
            description: l.description?.[lang],
            isVisible: l.isVisible,
            isHeadquarter: l.isHeadquarter,
            isSabbar: l.isSabbar,
            isMinistry: l.isMinistry
        }
    })
}

export const getLookupByKeyWithSearchText = (lookups: any, key: keyof PaLookups, lang: string): ({ value: string, label: string, searchText: string, description?: string, isVisible?: boolean, isHeadquarter?: boolean, isSabbar?: boolean, isMinistry?: boolean })[] => {
    if (!lookups) return;
    return lookups[key]?.map(l => {
        if (!l) return null;
        const en = l.content?.en ?? '';
        const ar = l.content?.ar ?? '';
        return {
            value: l.id,
            label: l.content[lang],
            searchText: `${en} ${ar}`.toLowerCase(),
            description: l.description?.[lang],
            isVisible: l.isVisible,
            isHeadquarter: l.isHeadquarter,
            isSabbar: l.isSabbar,
            isMinistry: l.isMinistry
        };
    });
}

export const matchesLookupSelectSearch = (
    option: { label?: string; value?: string; searchText?: string },
    searchKey: string
): boolean => {
    const input = searchKey?.toLowerCase()?.trim();
    if (!input) return true;
    if (option?.searchText?.includes(input)) return true;
    return (
        option?.label?.toLowerCase()?.includes(input) ||
        option?.value?.toLowerCase()?.includes(input) ||
        false
    );
}

export const shouldPartnerActivateAccountFirst = (partner: Partner): boolean => {
    if (!partner) return true;

    // allow some partners to unlock without subscription
    if (PARTNERS_ALLOWED_UNLOCKS_TRIAL.includes(partner.id)) {
        return false;
    }

    if (partner.status === PartnerStatus.UNDER_REVIEW || partner.status === PartnerStatus.REQUIRE_ADDITIONAL_INFO) return true;

    if (partner.currentSubscription === null || partner.currentSubscription.subscriptionStatus !== SubscriptionStatusEnum.ACTIVE) {
        return true;
    }

    return false;
}

export const getInitials = (name: string) => {

    if (!name) return '';

    let names = name.split(' '),
        initials = names[0].substring(0, 1).toUpperCase();

    if (names.length > 1) {
        initials += names[names.length - 1].substring(0, 1).toUpperCase();
    }
    return initials;
};


// Validate Saudi National ID
// Return -1 if the id is not correct, 1 for Saudi id OR 2 for Non-Saudis.
export function validateSAID(id: string): number {
    const type = id[0];
    const _idLength = 10;
    const _type1 = '1';
    const _type2 = '2';
    let sum = 0;
    id = id.trim();
    if (isNaN(parseInt(id)) || (id.length !== _idLength) || (type !== _type2 && type !== _type1)) {
        return -1;
    }
    for (let num = 0; num < 10; num++) {
        const digit = Number(id[num]);
        if (num % 2 === 0) {
            const doubled = digit * 2;
            const ZFOdd = `00${doubled}`.slice(-2);
            sum += Number(ZFOdd[0]) + Number(ZFOdd[1]);
        } else {
            sum += digit;
        }
    }
    return (sum % 10 !== 0) ? -1 : Number(type);
}

export const getExactExperienceString = (lang: 'en' | 'ar', exactExperience: { jobTitle: string, isHighlighted: boolean, months?: number, years?: number, specialization?: string }) => {
    if (!exactExperience || !exactExperience.jobTitle) return undefined;
    if (lang === 'en') {
        if (!exactExperience.months && !exactExperience.years) {
            return `Worked as ${exactExperience.jobTitle}`
        }
        return `Worked as ${exactExperience.jobTitle} for ${exactExperience.years ? `${exactExperience.years} ${exactExperience.years > 1 ? 'years' : 'year'}` : `${exactExperience.months} ${exactExperience.months > 1 ? 'months' : 'month'}`}`
    }

    if (!exactExperience.months && !exactExperience.years) {
        return `خبرة ${exactExperience.jobTitle}`
    }
    return `خبرة ${exactExperience.jobTitle} لمدة ${exactExperience.years ? `${exactExperience.years > 1 ? exactExperience.years : ''} ${exactExperience.years > 1 ? 'سنوات' : 'سنة'} ` : `${exactExperience.months} ${exactExperience.months > 1 ? 'اشهر' : 'شهر'} `}`
}

const getJobTitleString = (relevantExperiences: any, title: any) => {
    const specialization = relevantExperiences ? relevantExperiences.find(re => re.jobTitle === title)?.specialization : undefined;
    return specialization ? `${title} / ${specialization}` : title;
}

const getDurationString = (experience: any) => {
    const toDate = experience.toDate ?? Math.ceil(Date.now() / 1000);
    const duration = getDurationBetweenDates(experience.fromDate, toDate);
    const years = duration?.years > 0 ? duration.years : undefined;
    const months = duration?.months > 0 ? duration.months : undefined;
    const labelYears = years && years > 1 ? t("short_years") : t("short_year")
    const labelMonth = months && months > 1 ? t("short_months") : t("short_month")

    return `${years ? `${years} ${labelYears}` : ''} ${months ? `${months} ${labelMonth}` : ''}`
}

export const handleIntercom = () => {
    trackSupportChatInitiated();
    window?.Intercom?.("showMessages");
}

export const getHQLocationsList = (lookups, lang) => {
    const cityLookup = getLookupByKey(lookups, 'cityObjectIdLookup', lang);
    return cityLookup
        ? cityLookup.filter(({ isHeadquarter }) => isHeadquarter).map(({ value, label }) => ({
            value: value,
            label
        }))
        : [];
}

const normalizeUtmQueryValue = (value: string | null) =>
    value?.trim().toLowerCase().replace(/\/+$/, '') || null;

export const getUtmParamsFromUrl = () => {
    const urlParams = getPageSearchParams();
    const utm_source = normalizeUtmQueryValue(urlParams.get('utm_source'));
    const utm_medium = normalizeUtmQueryValue(urlParams.get('utm_medium'));
    const utm_campaign = normalizeUtmQueryValue(urlParams.get('utm_campaign'));
    const utm_content = normalizeUtmQueryValue(urlParams.get('utm_content'));
    const utm_term = normalizeUtmQueryValue(urlParams.get('utm_term'));
    const gclid = urlParams.get('gclid')?.trim().replace(/\/+$/, '') || null;

    if (utm_source || utm_medium || utm_campaign || utm_content || utm_term || gclid) {
        return { utm_source, utm_medium, utm_campaign, utm_content, utm_term, gclid };
    }

    return null;
}

export const safeParseUtmCookie = (raw: string): any => {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        try {
            return JSON.parse(decodeURIComponent(raw));
        } catch {
            return null;
        }
    }
}

export const getUtmDataFromCookies = () => {
    return readUtmDataFromCookies();
}

export const getUtmParamsFromCookie = () => {
    const utmData = getUtmDataFromCookies();
    const fromCookies = utmData?.lastUtm || utmData?.firstUtm || null;
    if (hasUtmSignal(fromCookies)) return fromCookies;
    return getUtmParamsFromUrl() || fromCookies || null;
}

export const getBrowser = (): { name: string, icon: string } => {
    if ((window.navigator.userAgent.indexOf("Opera") || window.navigator.userAgent.indexOf('OPR')) !== -1) {
        return { name: 'Opera', icon: 'generic-2.png' }
    } else if (window.navigator.userAgent.indexOf("Edg") !== -1) {
        return { name: 'Edge', icon: 'edge.png' }
    } else if (window.navigator.userAgent.indexOf("Chrome") !== -1) {
        return { name: 'Chrome', icon: 'chrome.png' }
    } else if (window.navigator.userAgent.indexOf("Safari") !== -1) {
        return { name: 'Safari', icon: 'safari-2.png' }
    } else if (window.navigator.userAgent.indexOf("Firefox") !== -1) {
        return { name: 'Firefox', icon: 'firefox.png' }
    } else {
        return { name: 'Unkown', icon: 'generic-2.png' }
    }
}

export const getMobileDetect = (userAgent: NavigatorID['userAgent']) => {
    const isAndroid = () => Boolean(userAgent.match(/Android/i))
    const isIos = () => Boolean(userAgent.match(/iPhone|iPad|iPod/i))
    const isSafari = () => Boolean(userAgent.match(/Version\/[\d\.]+.*Safari/i))
    const isOpera = () => Boolean(userAgent.match(/Opera Mini/i))
    const isWindows = () => Boolean(userAgent.match(/IEMobile/i))
    const isSSR = () => Boolean(userAgent.match(/SSR/i))
    const isMobile = () => Boolean(isAndroid() || isIos() || isOpera() || isWindows())
    const isDesktop = () => Boolean(!isMobile() && !isSSR())
    const isIOSSafari = () => Boolean(isIos() && isSafari())
    return {
        isMobile,
        isDesktop,
        isAndroid,
        isIos,
        isSafari,
        isIOSSafari,
        isSSR,
    }
}

export const titleCase = (str: string) => {
    if (!str) return '';
    return str.replace(
        /\w\S*/g,
        text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
    );
}

export const objectToQueryString = (obj) => {
    const keys = Object.keys(obj);
    const keyValuePairs = keys.map(key => {
        return encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]);
    });
    return keyValuePairs.join('&');
}

export const isEmptyObject = (obj) => {
    if (!obj) return true;
    return Object.values(obj).filter(f => {
        if (Array.isArray(f)) {
            if (f.length) {
                return true
            } else {
                return false;
            }
        } else if (f) {
            return true
        } else {
            return false;
        }
    }).length === 0
}

export const updateObjectInList = (items: any[], id: string, obj: any): any[] => {
    if (!items) return [];

    if (!obj || !id) return items;

    return items.map(item => {
        if (item.externalId === id) {
            return {
                ...item,
                ...obj,
            };
        }
        return item;
    });
}

export const updateObjectsInList = (items: any[], ids: string[], obj: any): any[] => {
    if (!items) return [];

    if (!obj || !Array.isArray(ids) || ids.length === 0) return items;

    const idsSet = new Set(ids);

    return items.map(item => {
        if (idsSet.has(item.externalId)) {
            return {
                ...item,
                ...obj,
            };
        }
        return item;
    });
}

export const roundToTenThousand = (num: number): number => {
    return Math.round(num / 10000) * 10000;
}

export const roundToThousand = (num: number): number => {
    return Math.round(num / 1000) * 1000;
}

export const getJobSeekersCount = (num: number): string => {
    if (!num) return '850,000';
    if (!isProduction()) return '850,000';
    return (Math.round(num / 10000) * 10000).toLocaleString();
}

export const displayJobLocation = (jobInfo, lang) => {
    return jobInfo?.locationInfo?.localizedAddress?.[lang] || `${jobInfo?.locationInfo?.localizedCity?.[lang] ?
        jobInfo?.locationInfo?.localizedCity?.[lang] + ', ' : ''}${jobInfo?.locationInfo?.localizedDistrict?.[lang] || ''}` || jobInfo?.locationInfo?.address;
}

export const removeDuplicatesByKey = (array, key) => {
    if (!array) return [];
    const seen = new Set();
    return array.filter((item) => {
        const keyValue = item[key];
        if (seen.has(keyValue)) {
            return false;
        }
        seen.add(keyValue);
        return true;
    });
}

export const getLang = () => {
    return localStorage?.getItem('i18nextLng')?.includes('en') ? 'en' : 'ar';
}

export const getSubscriptionModalTitleAndSubtitle = (frontendSource: FrontendPaymentSource, isInModal: boolean): { title: string, subtitle: string } => {
    let title = 'popup_title_default';
    let subtitle = '';

    if (!isInModal) {
        return {
            title: 'popup_title_default',
            subtitle: 'jobseekers_across_saudi'
        }
    }

    if (frontendSource === FrontendPaymentSource.APPLICANTS_LIST) {
        title = 'popup_title_view_all_applicants';
    }

    if (frontendSource === FrontendPaymentSource.RECOMMENDATIONS_LIST) {
        title = 'popup_title_view_all_recommendations';
    }

    if (frontendSource === FrontendPaymentSource.APPLICANT_DETAILS_SUBSCRIBED || frontendSource === FrontendPaymentSource.APPLICANT_DETAILS || frontendSource === FrontendPaymentSource.CHAT) {
        title = 'popup_title_unlock_applicant';
    }

    if (frontendSource === FrontendPaymentSource.CV_SEARCH) {
        title = 'popup_title_unlock_applicant';
    }

    if (frontendSource === FrontendPaymentSource.VIDEO_CV) {
        title = 'popup_title_watch_video_cv';
    }

    if (frontendSource === FrontendPaymentSource.PDF_CV_APPLICANT) {
        title = 'popup_title_pdf_cv';
    }

    if (frontendSource === FrontendPaymentSource.JOB_LIST || frontendSource === FrontendPaymentSource.BILLING_HISTORY_UPGRADE) {
        title = 'popup_title_post_new_job';
    }

    if (frontendSource === FrontendPaymentSource.JOB_LIST_SUBSCRIBED) {
        title = 'popup_title_post_new_job_subscribed';
    }

    if (frontendSource === FrontendPaymentSource.SAUD_VETTING) {
        title = 'popup_title_saud';
    }

    if (frontendSource === FrontendPaymentSource.PROMOTE_JOB) {
        title = 'popup_title_promote_job';
    }

    if (frontendSource === FrontendPaymentSource.ADVANCED_FILTERS) {
        title = 'popup_title_advanced_filters';
    }


    return {
        title,
        subtitle
    }
}

export const replaceRiyalInString = (str: string) => {
    return str?.replace(/<NEW_RIYAL>/g, ` <img src="${RIYAL_URL}" alt="SR" class="riyal-html"/> `)?.replace(/NEW_RIYAL/g, ` <img src="${RIYAL_URL}" alt="SR" class="riyal-html"/> `)?.replace(/<نيو ريال>/g, ` <img src="${RIYAL_URL}" alt="SR" class="riyal-html"/> `);
}

export const replaceRiyalInString2 = (str: string) => {
    return str?.replace('<NEW_RIYAL>', 'SR')?.replace('<NEW_RIYAL>', 'SR');
}

// Debounce utility
export function debounce(func, delay) {
    let timer;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), delay);
    };
}

export const countTextCharacters = (htmlString: string) => {
    if (htmlString) return 0;
    // Remove HTML tags
    let text = htmlString.replace(/<[^>]*>/g, '');

    // Decode common HTML entities
    text = text.replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/&nbsp;/g, ' ')
        .replace(/&hellip;/g, '...')
        .replace(/&mdash;/g, '—')
        .replace(/&ndash;/g, '–')
        .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec))
        .replace(/&#x([0-9a-f]+);/gi, (match, hex) => String.fromCharCode(parseInt(hex, 16)));

    return text.length;
}



export const isStringMobileOrEmail = (input: string): InvitationStringType => {
    if (!input) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^\+?[0-9]{7,15}$/; // Accepts optional '+' and 7-15 digits

    if (emailRegex.test(input)) {
        return InvitationStringType.EMAIL;
    } else if (mobileRegex.test(input)) {
        return InvitationStringType.MOBILE;
    } else {
        return null;
    }
}

export const getProgressColor = (variant: 'success' | 'error' | 'info') => {
    const colors = {
        success: { progress: '#399d81', background: '#F1F9F6', border: '#afe4d1' },
        error: { progress: '#f7cfd1', background: '#FCF6F6', border: '#f7cfd1' },
        info: { progress: '#399d81', background: '#fafafa', border: '#dedfe3' },
    };

    return colors[variant] || colors.success;
}

export const generateUuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
    });
}

export const formatSaudiNumber = (number: string) => {
    if (!number) return '';
    // Remove any non-digit characters just in case
    const digits = number.replace(/\D/g, '');

    // Check if it starts with country code 966 and is followed by 9 digits
    if (digits.startsWith('966') && digits.length === 12) {
        const local = '0' + digits.slice(3); // replace '966' with '0'
        return `${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
    }

    return number;
}

export const formatWhatsappNumber = (number: string) => {
    if (!number) return '';
    // Remove any non-digit characters just in case
    const digits = number.replace(/\D/g, '');

    // Format Saudi numbers (966XXXXXXXXX) as "(+966) 05X XXX XXXX"
    if (digits.startsWith('966') && digits.length === 12) {
        const local = digits.slice(3);
        return `(+966) ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
    }

    // Format Egyptian mobiles (20 1X XXXXXXXX) as "(+20) 01X XXXX XXXX"
    if (
        (digits.startsWith('20') && digits.length === 12 && digits[2] === '1') // 201XXXXXXXXX
    ) {
        const local = digits.slice(2);
        return `(+20) ${local.slice(0, 3)} ${local.slice(3, 7)} ${local.slice(7)}`;
    }

    return number;
}

export const getDateRangePlusMonths = (monthsToAdd: number, locale: 'en' | 'ar' = 'ar') => {
    const now = new Date();
    const futureDate = new Date(now);
    futureDate.setMonth(futureDate.getMonth() + monthsToAdd);

    // Handle month overflow (e.g., Jan 31 → Feb 28)
    if (futureDate.getDate() !== now.getDate()) {
        futureDate.setDate(0);
    }

    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    const localeCode = locale === 'ar' ? 'ar-EG' : 'en-GB';

    const start = now.toLocaleDateString(localeCode, options);
    const startYear = now.toLocaleDateString(localeCode, { year: 'numeric' });
    const end = futureDate.toLocaleDateString(localeCode, options);
    const endYear = futureDate.toLocaleDateString(localeCode, { year: 'numeric' });

    return `${start}, ${startYear} - ${end}, ${endYear}`;
};

export const arabicToEnglishNumbers = (input: string): string => {
    if (!input) return '';

    const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

    return input.replace(/[٠-٩]/g, (digit) => {
        return String(arabicNumbers.indexOf(digit));
    });
}

export const getBookDemoLink = (partner: Partner, partnerUser: PartnerUser) => {
    return `https://2f7mbn.share-eu1.hsforms.com/2-PYlEyKFTCOkANwW7NJicw?email=${encodeURI(partnerUser?.email)}`;
    // return `https://docs.google.com/forms/d/e/1FAIpQLSdpwkXJrcrpZssg_t9LtBgvxTzW-GaZQ7R5FyWHeBSHrXoNhw/viewform?usp=pp_url&entry.1749406959=${partner?.adminFullName}&entry.921247767=${partner?.name}&entry.1871791487=${partnerUser?.email}`
}

export const isArabicMoreInString = (s: string): boolean => {
    const arabic = (s.match(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0870-\u089F\uFB50-\uFDFF\uFE70-\uFEFF]/g) || []).length;
    const english = (s.match(/[A-Za-z]/g) || []).length;
    return arabic > english;
}

export const isDateBetween = (start: string, end: string) => {
    const toLocalMidnight = (s: string): number => {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
        if (!m) return NaN;
        const [, y, mo, d] = m;
        return new Date(Number(y), Number(mo) - 1, Number(d)).getTime();
    };

    let a = toLocalMidnight(start);
    let b = toLocalMidnight(end);
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    if (a > b) [a, b] = [b, a]; // support reversed

    const endOfEndDay = new Date(b);
    endOfEndDay.setHours(23, 59, 59, 999);

    const now = Date.now();
    return now >= a && now <= endOfEndDay.getTime();
}

export const getTopUniqueByTitle = (allJobs: FullTimeJob[], max: number) => {
    const seen = new Set<string>();
    const result: FullTimeJob[] = [];

    for (const job of allJobs) {
        const key = job.jobTitleId;
        if (!seen.has(key)) {
            seen.add(key);
            result.push(job);
            if (result.length >= max) break;
        }
    }


    return result;
};

export const showMinimumSalaryNote = (salary: number, salaryFrequency: SalaryFrequencyEnum, nationality: SaudiNationalityEnum, contractType: ContractTypeV2) => {

    if (!salary || !salaryFrequency || !nationality || !contractType) {
        return false;
    }

    if (salaryFrequency === SalaryFrequencyEnum.MONTHLY) {

        if (nationality === SaudiNationalityEnum.SAUDI) {
            if (contractType === ContractTypeV2.FULLTIME) {
                return salary < 4000;
            } else {
                return salary < 2000;
            }
        } else if (nationality === SaudiNationalityEnum.NONSAUDI) {
            if (contractType === ContractTypeV2.FULLTIME) {
                return salary < 2000;
            } else {
                return salary < 500;
            }
        }

    } else if (salaryFrequency === SalaryFrequencyEnum.DAILY) {
        return salary < 100;

    } else if (salaryFrequency === SalaryFrequencyEnum.HOURLY) {

        if (nationality === SaudiNationalityEnum.SAUDI) {
            return salary < 25;

        } else if (nationality === SaudiNationalityEnum.NONSAUDI) {
            return salary < 15;

        }
    }
    return false;

}

export const returnContractTypesText = (contractType: ContractTypeV2) => {
    if (contractType === ContractTypeV2.FULLTIME) {
        return { en: 'Full Time', ar: 'دوام كامل' }
    }

    if (contractType === ContractTypeV2.PARTTIME) {
        return { en: 'Part Time', ar: 'دوام جزئي' }
    }

    if (contractType === ContractTypeV2.INTERNSHIP) {
        return { en: 'Training / Internship', ar: 'تدريب' }
    }

    if (contractType === ContractTypeV2.GIG) {
        return { en: 'Shift Based', ar: 'دوام مرن (بالساعة)' }
    }

    if (contractType === ContractTypeV2.SEASONAL) {
        return { en: 'Temp/Seasonal', ar: 'عمل مؤقت' }
    }

    return { en: 'Full Time', ar: 'دوام كامل"' }

}

export const getCareersDetailsPageMetadata = (lang: string, employerName: string, jobTitle: string, contractType: string) => {
    if (lang === 'en') {
        return {
            title: `Join the ${employerName} Team!`,
            description: `We're looking for a ${contractType} ${jobTitle}`,
            shareText: `Apply to ${jobTitle} at ${employerName}`
        }
    }

    return {
        title: `انضم إلى فريق ${employerName} !`,
        description: `نبحث عن ${contractType} ${jobTitle}`,
        shareText: `قدم على ${jobTitle} في ${employerName}`
    }
}

export const checkSource = (selectedApplicant: ApplicantListItem, source: OfflineApplicationSourceEnum) => {
    const sourceLowerCase = source?.toLowerCase();

    return (selectedApplicant?.source?.toLowerCase() === sourceLowerCase || selectedApplicant?.utmMedium?.toLowerCase() === sourceLowerCase || selectedApplicant?.referrer?.toLowerCase() === sourceLowerCase || selectedApplicant?.utmSource?.toLowerCase() === sourceLowerCase || selectedApplicant?.utmMedium?.toLowerCase()?.includes(sourceLowerCase) || selectedApplicant?.utmSource?.toLowerCase()?.includes(sourceLowerCase) || selectedApplicant?.referrer?.toLowerCase()?.includes(sourceLowerCase));
}

export const getOfflineApplicationSourceAndIcon = (selectedApplicant: ApplicantListItem): { source: string, icon: string, fullSourceText: string } => {
    if (!selectedApplicant?.isOffline) return null;
    let fullSourceText = ``;
    let newLine = '';

    if (selectedApplicant?.source && selectedApplicant?.source !== 'WEB') {
        fullSourceText += `Source: ${titleCase(selectedApplicant?.source)}\n `;
    }

    if (selectedApplicant?.utmSource && selectedApplicant?.utmSource !== 'WEB') {
        fullSourceText += `UTM Source: ${titleCase(selectedApplicant?.utmSource)}\n `;
    }
    if (selectedApplicant?.utmMedium && selectedApplicant?.utmMedium !== 'WEB') {
        fullSourceText += `UTM Medium: ${titleCase(selectedApplicant?.utmMedium)}\n `;
    }
    if (selectedApplicant?.utmData?.utm_campaign && selectedApplicant?.utmData?.utm_campaign !== 'WEB') {
        fullSourceText += `UTM Campaign: ${selectedApplicant?.utmData?.utm_campaign}\n`;
    }

    if (!fullSourceText) {
        fullSourceText = t('career_website');
        newLine = '\n';
    }

    if (selectedApplicant?.referrer && selectedApplicant?.referrer !== 'WEB') {
        fullSourceText += `${newLine}Referrer: ${selectedApplicant?.referrer?.toLowerCase()}\n`;
    }

    let res = { source: t('career_website'), icon: careerWebsiteIcon, fullSourceText: fullSourceText }

    const isTiktok = checkSource(selectedApplicant, OfflineApplicationSourceEnum.TIKTOK);
    const isLinkedin = checkSource(selectedApplicant, OfflineApplicationSourceEnum.LINKEDIN);
    const isBayt = checkSource(selectedApplicant, OfflineApplicationSourceEnum.BAYT);
    const isGoogle = checkSource(selectedApplicant, OfflineApplicationSourceEnum.GOOGLE);
    const isIndeed = checkSource(selectedApplicant, OfflineApplicationSourceEnum.INDEED);
    const isNaukriGulf = checkSource(selectedApplicant, OfflineApplicationSourceEnum.NAUKRI_GULF);
    const isGlassdoor = checkSource(selectedApplicant, OfflineApplicationSourceEnum.GLASSDOOR);
    const isMonsterGulf = checkSource(selectedApplicant, OfflineApplicationSourceEnum.MONSTER_GULF);
    const isGulfTalent = checkSource(selectedApplicant, OfflineApplicationSourceEnum.GULFTALENT);
    const isFacebook = checkSource(selectedApplicant, OfflineApplicationSourceEnum.FACEBOOK);
    const isTwitter = checkSource(selectedApplicant, OfflineApplicationSourceEnum.TWITTER) || selectedApplicant?.utmMedium?.toLowerCase()?.includes('t.co') || selectedApplicant?.utmSource?.toLowerCase()?.includes('t.co') || selectedApplicant?.referrer?.toLowerCase()?.includes('t.co') || selectedApplicant?.utmMedium?.toLowerCase()?.includes('x.com') || selectedApplicant?.utmSource?.toLowerCase()?.includes('x.com') || selectedApplicant?.referrer?.toLowerCase()?.includes('x.com');
    const isInstagram = checkSource(selectedApplicant, OfflineApplicationSourceEnum.INSTAGRAM);
    const isSnapchat = checkSource(selectedApplicant, OfflineApplicationSourceEnum.SNAPCHAT);
    const isWhatsapp = checkSource(selectedApplicant, OfflineApplicationSourceEnum.WHATSAPP);
    const isTelegram = checkSource(selectedApplicant, OfflineApplicationSourceEnum.TELEGRAM);
    const isEmail = checkSource(selectedApplicant, OfflineApplicationSourceEnum.EMAIL);
    const isYoutube = checkSource(selectedApplicant, OfflineApplicationSourceEnum.YOUTUBE);
    const isSaudiJobs = checkSource(selectedApplicant, OfflineApplicationSourceEnum.SAUDI_JOBS);
    const isForsah = checkSource(selectedApplicant, OfflineApplicationSourceEnum.FORSAH);
    const isTanqeeb = checkSource(selectedApplicant, OfflineApplicationSourceEnum.TANQEEB);
    const isWeb = checkSource(selectedApplicant, OfflineApplicationSourceEnum.WEB);

    if (isTiktok) {
        res = { source: t('tiktok'), icon: sourceTiktokIcon, fullSourceText: fullSourceText }
    }

    if (isLinkedin) {
        res = { source: t('linkedIn'), icon: sourceLinkedinIcon, fullSourceText: fullSourceText }
    }

    if (isBayt) {
        res = { source: t('bayt'), icon: sourceBaytIcon, fullSourceText: fullSourceText }
    }

    if (isGoogle) {
        res = { source: t('google'), icon: sourceGoogleIcon, fullSourceText: fullSourceText }
    }

    if (isIndeed) {
        res = { source: t('indeed'), icon: sourceIndeedIcon, fullSourceText: fullSourceText }
    }

    if (isNaukriGulf) {
        res = { source: t('naukri_gulf'), icon: sourceNaukriGulfIcon, fullSourceText: fullSourceText }
    }

    if (isGlassdoor) {
        res = { source: t('glassdoor'), icon: sourceGlassdoorIcon, fullSourceText: fullSourceText }
    }

    if (isMonsterGulf) {
        res = { source: t('monster_gulf'), icon: sourceMonsterGulfIcon, fullSourceText: fullSourceText }
    }

    if (isGulfTalent) {
        res = { source: t('gulf_talent'), icon: sourceGulfTalentIcon, fullSourceText: fullSourceText }
    }

    if (isFacebook) {
        res = { source: t('facebook'), icon: sourceFacebookIcon, fullSourceText: fullSourceText }
    }

    if (isTwitter) {
        res = { source: t('twitter'), icon: sourceTwitterIcon, fullSourceText: fullSourceText }
    }

    if (isInstagram) {
        res = { source: t('instagram'), icon: sourceInstagramIcon, fullSourceText: fullSourceText }
    }

    if (isSnapchat) {
        res = { source: t('snapchat'), icon: sourceSnapchatIcon, fullSourceText: fullSourceText }
    }

    if (isWhatsapp) {
        res = { source: t('whatsapp'), icon: sourceWhatsappIcon, fullSourceText: fullSourceText }
    }

    if (isTelegram) {
        res = { source: t('telegram'), icon: sourceTelegramIcon, fullSourceText: fullSourceText }
    }

    return res;
}

export const getLocationString = (applicant: ApplicantListItem) => {
    const applicantCity = applicant?.city && applicant?.city !== 'Other' && applicant?.city !== 'أخرى' ? applicant?.city : '';
    return applicant?.district || applicantCity ? `${applicant?.district || ''}${applicant?.district && applicantCity ? ', ' : ''}${applicantCity}` : '';
}

export const getMatchingFields = (applicant: ApplicantListItem) => {
    const { vettingStatus, aiMatchingInitialDecision, aiDecision } = applicant;
    const interviewIcon = vettingStatus === AiInterviewStatusEnum.PENDING || vettingStatus === AiInterviewStatusEnum.IN_PROGRESS || vettingStatus === AiInterviewStatusEnum.FINISHED ? interviewFinishedIcon : interviewDeclinedIcon;
    const matchingIcon = aiMatchingInitialDecision === AiInterviewDecisionEnum.GOOD_FIT ? greenThumbsUp : aiMatchingInitialDecision === AiInterviewDecisionEnum.MAYBE ? yelloMaybe : greyThumbsDown;
    const matchingIconClass = aiMatchingInitialDecision === AiInterviewDecisionEnum.GOOD_FIT ? 'pos-rel-minus-2' : aiMatchingInitialDecision === AiInterviewDecisionEnum.MAYBE ? 'pos-rel-minus-1' : '';
    const aiIcon = aiDecision === AiInterviewDecisionEnum.GOOD_FIT ? greenThumbsUp : aiDecision === AiInterviewDecisionEnum.MAYBE ? yelloMaybe : greyThumbsDown;
    const aiIconClass = aiDecision === AiInterviewDecisionEnum.GOOD_FIT ? 'pos-rel-minus-2' : aiDecision === AiInterviewDecisionEnum.MAYBE ? 'pos-rel-minus-1' : '';

    return { interviewIcon, matchingIcon, aiIcon, matchingIconClass, aiIconClass };
}

export const buildApplicantsFiltersObjectForApi = (initialFilters: ApplicantsFilter, initialBuiltFilters: ApplicantsFilter, selectedFilters: ApplicantsFiltersEnumObject): { builtFilters: ApplicantsFilter, hasError: boolean, filterErrors: ApplicantsFiltersEnumObject, hasFiltersChanged: boolean, needToClearFilters: boolean } => {
    let builtFilters: ApplicantsFilter = { ...initialBuiltFilters };
    let filterErrors: ApplicantsFiltersEnumObject = {};
    let hasFiltersChanged = false;
    let needToClearFilters = false;

    const currentFiltersHashKey = getCacheKeyForObject(initialFilters, 'vetting');
    let hasError = false;

    for (const [key, value] of Object.entries(selectedFilters)) {
        if (key === LocalFilterEnum.EXPERIENCE) {
            builtFilters.minYearsOfExperience = value['min'];
            builtFilters.maxYearsOfExperience = value['max'];
            if (value['min'] && value['max'] && value['max'] >= value['min']) {
                filterErrors = { ...filterErrors, [LocalFilterEnum.EXPERIENCE]: undefined };
            } else if (value['min'] && value['max'] && value['max'] < value['min']) {
                filterErrors = { ...filterErrors, [LocalFilterEnum.EXPERIENCE]: true };
                hasError = true;
            }
        } else if (key === LocalFilterEnum.NATIONALITY) {
            const nationalityArray = [];
            if (value[NationalityCategory.SAUDI] === true) {
                nationalityArray.push(NationalityCategory.SAUDI);
            }
            if (value[NationalityCategory.NON_SAUDI] === true) {
                nationalityArray.push(NationalityCategory.NON_SAUDI);
            }

            if (nationalityArray.length === 2 || nationalityArray.length === 0) {
                builtFilters.saudisOnly = null;
            } else if (nationalityArray.includes(NationalityCategory.SAUDI)) {
                builtFilters.saudisOnly = true;
            } else if (nationalityArray.includes(NationalityCategory.NON_SAUDI)) {
                builtFilters.saudisOnly = false;
            }

        } else if (key === LocalFilterEnum.EDUCATION_LEVEL) {
            const educationArray = [];
            if (value[EducationLevel.MIDDLE_SCHOOL_BELOW] === true) {
                educationArray.push(EducationLevel.MIDDLE_SCHOOL_BELOW);
            }
            if (value[EducationLevel.HIGH_SCHOOL] === true) {
                educationArray.push(EducationLevel.HIGH_SCHOOL);
            }
            if (value[EducationLevel.DIPLOMA] === true) {
                educationArray.push(EducationLevel.DIPLOMA);
            }
            if (value[EducationLevel.BACHELOR] === true) {
                educationArray.push(EducationLevel.BACHELOR);
            }
            if (value[EducationLevel.MASTERS_PHD] === true) {
                educationArray.push(EducationLevel.MASTERS_PHD);
            }

            if (educationArray.length > 0) {
                builtFilters.educationLevels = educationArray;
            }

        } else if (key === LocalFilterEnum.AGE) {
            builtFilters.minAge = value?.['min'];
            builtFilters.maxAge = value?.['max'];
            if (value?.['min'] && value?.['max'] && value?.['max'] >= value?.['min']) {
                filterErrors = { ...filterErrors, [LocalFilterEnum.AGE]: undefined };
            } else if (value?.['min'] && value?.['max'] && value?.['max'] < value?.['min']) {
                filterErrors = { ...filterErrors, [LocalFilterEnum.AGE]: true };
                hasError = true;
            }

        } else if (key === LocalFilterEnum.GENDER) {
            const genderArray = [];
            if (value[GenderEnum.MALE] === true) {
                genderArray.push(GenderEnum.MALE);
            }
            if (value[GenderEnum.FEMALE] === true) {
                genderArray.push(GenderEnum.FEMALE);
            }

            if (genderArray.length > 0) {
                builtFilters.genders = genderArray;
            }

        } else if (key === LocalFilterEnum.PREFERRED_CONTRACT_TYPE) {
            const contractTypeArray = [];
            if (value[ContractTypeV2.FULLTIME] === true) {
                contractTypeArray.push(ContractTypeV2.FULLTIME);
            }
            if (value[ContractTypeV2.PARTTIME] === true) {
                contractTypeArray.push(ContractTypeV2.PARTTIME);
            }
            if (value[ContractTypeV2.SEASONAL] === true) {
                contractTypeArray.push(ContractTypeV2.SEASONAL);
            }
            if (value[ContractTypeV2.INTERNSHIP] === true) {
                contractTypeArray.push(ContractTypeV2.INTERNSHIP);
            }

            if (contractTypeArray.length > 0) {
                builtFilters.contractTypes = contractTypeArray;
            }

        } else if (key === LocalFilterEnum.STATUS) {
            const statusArray = [];
            builtFilters.stageExternalIds = Object.keys(value ?? {}) as any;
            // if (value?.[ApplicationStatusEnum.PENDING] === true) {
            //     statusArray.push(ApplicationStatusEnum.PENDING);
            // }

            // if (value?.[ApplicationStatusEnum.SHORT_LISTED] === true) {
            //     statusArray.push(ApplicationStatusEnum.SHORT_LISTED);
            // }

            // if (value?.[ApplicationStatusEnum.FINALIST] === true) {
            //     statusArray.push(ApplicationStatusEnum.FINALIST);
            // }

            // if (value?.[ApplicationStatusEnum.NOT_SELECTED] === true) {
            //     statusArray.push(ApplicationStatusEnum.NOT_SELECTED);
            // }

            // if (statusArray.length > 0) {
            //     builtFilters.statuses = statusArray;
            // }

        } else if (key === LocalFilterEnum.SALARY_EXPECTATION) {
            builtFilters.minSalary = value?.['min'];
            builtFilters.maxSalary = value?.['max'];
            if (value?.['min'] && value?.['max'] && value?.['max'] >= value?.['min']) {
                filterErrors = { ...filterErrors, [LocalFilterEnum.SALARY_EXPECTATION]: undefined };
            } else if (value?.['min'] && value?.['max'] && value?.['max'] < value?.['min']) {
                filterErrors = { ...filterErrors, [LocalFilterEnum.SALARY_EXPECTATION]: true };
                hasError = true;
            }

        } else if (key === LocalFilterEnum.APPLICANTS) {
            // TODO should prevent both from being checked at the same time
            if (value[ApplicantRelevancyEnum.RELEVANT] === true) {
                builtFilters.relevantOnly = true;
            } else if (value[ApplicantRelevancyEnum.IRRELEVANT] === true) {
                builtFilters.relevantOnly = false;
            } else {
                builtFilters.relevantOnly = undefined;
            }

            // Quick-filter `applicants` must only override API fields when that toggle is present.
            // MH builds a sparse `applicants` object (see MhFilters.buildApplicantsObject): omitted keys
            // leave fields for OTHER_FILTERS / modal (e.g. approvedVideo) intact instead of clearing them.
            if (value[ApplicantRelevancyEnum.UNREAD] === true) {
                builtFilters.unReadOnly = true;
            } else if (Object.prototype.hasOwnProperty.call(value, ApplicantRelevancyEnum.UNREAD)) {
                builtFilters.unReadOnly = undefined;
            }

            if (value['approvedVideo'] === true) {
                builtFilters.approvedVideo = true;
            } else if (Object.prototype.hasOwnProperty.call(value, 'approvedVideo')) {
                builtFilters.approvedVideo = undefined;
            }

            if (value['completedSaudInterview'] === true) {
                builtFilters.completedSaudInterview = true;
            } else if (Object.prototype.hasOwnProperty.call(value, 'completedSaudInterview')) {
                builtFilters.completedSaudInterview = undefined;
            }

            if (value['excludePreviouslyRejected'] === true) {
                builtFilters.excludePreviouslyRejected = true;
            } else if (Object.prototype.hasOwnProperty.call(value, 'excludePreviouslyRejected')) {
                builtFilters.excludePreviouslyRejected = undefined;
            }

            if (value['canJoinImmediately'] === true) {
                builtFilters.canJoinImmediately = true;
            } else if (Object.prototype.hasOwnProperty.call(value, 'canJoinImmediately')) {
                builtFilters.canJoinImmediately = undefined;
            }

            if (value['hasSpecialNeed'] === true) {
                builtFilters.hasSpecialNeed = true;
            } else if (Object.prototype.hasOwnProperty.call(value, 'hasSpecialNeed')) {
                builtFilters.hasSpecialNeed = undefined;
            }

        } else if (key === LocalFilterEnum.SAUDI_INTERVIEW_STATUS) {
            const interviewStatusArray = [];
            const interviewDecisionsArray = [];

            if (value?.[AiInterviewDecisionEnum.GOOD_FIT] === true) {
                interviewDecisionsArray.push(AiInterviewDecisionEnum.GOOD_FIT);
            }
            if (value?.[AiInterviewDecisionEnum.MAYBE] === true) {
                interviewDecisionsArray.push(AiInterviewDecisionEnum.MAYBE);
            }
            if (value?.[AiInterviewDecisionEnum.MISMATCH] === true) {
                interviewDecisionsArray.push(AiInterviewDecisionEnum.MISMATCH);
            }
            if (value?.[AiInterviewDecisionEnum.NO_INTERVIEW] === true) {
                interviewStatusArray.push(AiInterviewDecisionEnum.NO_INTERVIEW);
                interviewStatusArray.push(AiInterviewStatusEnum.DECLINED, AiInterviewStatusEnum.PENDING);
            }

            if (interviewStatusArray.length > 0) {
                builtFilters.interviewStatuses = interviewStatusArray;
            } else {
                builtFilters.interviewStatuses = undefined;
            }

            if (interviewDecisionsArray.length > 0) {
                builtFilters.interviewDecisions = interviewDecisionsArray;
            } else {
                builtFilters.interviewDecisions = undefined;
            }

        } else if (key === LocalFilterEnum.ENGLISH_PROFICIENCY) {
            const englishArray = [];
            if (value[LanguageProficiencyEnum.BEGINNER] === true) {
                englishArray.push(LanguageProficiencyEnum.BEGINNER);
            }
            if (value[LanguageProficiencyEnum.INTERMEDIATE] === true) {
                englishArray.push(LanguageProficiencyEnum.INTERMEDIATE);
            }
            if (value[LanguageProficiencyEnum.ADVANCED] === true) {
                englishArray.push(LanguageProficiencyEnum.ADVANCED);
            }

            if (englishArray.length > 0) {
                builtFilters.englishLevels = englishArray;
            }

        } else if (key === LocalFilterEnum.OTHER_FILTERS) {
            if (value['hasSpecialNeed'] === true) {
                builtFilters.hasSpecialNeed = true;
            } else if (Object.prototype.hasOwnProperty.call(value, 'hasSpecialNeed')) {
                builtFilters.hasSpecialNeed = undefined;
            }
            if (value['approvedVideo'] === true) {
                builtFilters.approvedVideo = true;
            }
            if (value['approvedPhoto'] === true) {
                builtFilters.approvedPhoto = true;
            }
        } else if (key === LocalFilterEnum.INDUSTRIES) {
            if (value['industries']?.length > 0) {
                builtFilters.industries = value['industries'];
            } else {
                builtFilters.industries = undefined;
            }
        } else if (key === LocalFilterEnum.DISTRICTS) {
            const districtValue = value as any;
            if (districtValue?.['districts']?.length > 0) {
                builtFilters.districts = districtValue?.['districts'];
            } else if (districtValue?.length > 0) {
                builtFilters.districts = districtValue;
            } else {
                builtFilters.districts = undefined;
            }
        } else if (key === LocalFilterEnum.JOB_LOCATION) {
            const jobLocationValue = value as any;
            if (jobLocationValue?.['jobLocationIds']?.length > 0) {
                builtFilters.jobLocationIds = jobLocationValue?.['jobLocationIds'];
            } else if (jobLocationValue?.length > 0) {
                builtFilters.jobLocationIds = jobLocationValue;
            } else {
                builtFilters.jobLocationIds = undefined;
            }
        } else if (key === LocalFilterEnum.ACTIVE_IN_LAST_DAYS) {
            if (value) {
                builtFilters.activeInLastDays = value as number;
            } else {
                builtFilters.activeInLastDays = undefined;
            }
        } else if (key === LocalFilterEnum.SEARCH_RADIUS) {
            if (value) {
                builtFilters.searchRadius = value as number;
            } else {
                builtFilters.searchRadius = undefined;
            }
        } else if (key === LocalFilterEnum.SCREENING_QUESTION_FILTERS) {
            builtFilters.screeningQuestionFilters = value?.length ? value : undefined;
        }
    }

    const newFiltersHashKey = getCacheKeyForObject(builtFilters);
    // only update filters if they changed
    if (currentFiltersHashKey !== newFiltersHashKey) {
        hasFiltersChanged = true;

        if (currentFiltersHashKey && !newFiltersHashKey) {
            needToClearFilters = true;
        }
    }

    return {
        builtFilters,
        hasError,
        filterErrors,
        hasFiltersChanged,
        needToClearFilters
    };
}

export const getWhatsappLink = (selectedApplicant: ApplicantListItem, partnerName: string, lang: string, jobTitleName: string) => {
    const text = lang === 'en' ? `Hi ${selectedApplicant?.firstName || ''} ${selectedApplicant?.lastName || ''} 👋,
I’m the recruiter from ${partnerName}. I reviewed your application for the “${jobTitleName}” position via Sabbar, and we’d like to get in touch with you.`
        : `أهلا ${selectedApplicant?.firstName || ''} ${selectedApplicant?.lastName || ''}👋،
أنا مسؤول التوظيف من ${partnerName}. اطلعت على تقديمك للوظيفة “${jobTitleName}” عبر صبّار. وودنا نتواصل معك.`;


    return `https://api.whatsapp.com/send/?phone=${selectedApplicant.mobile}&text=${text}`;
}

export const allWorkplaceTypes = [JobPostWorkplaceTypeEnum.ON_SITE, JobPostWorkplaceTypeEnum.FIELD, JobPostWorkplaceTypeEnum.REMOTE];


export const getShareJobUrl = (platform: 'linkedin' | 'twitter' | 'whatsapp' | 'instagram' | 'copy', jobDetails: FullTimeJobDetails, lang: string, partner: Partner) => {
    let url = '';
    const shareText = getCareersDetailsPageMetadata(lang, partner?.name, jobDetails?.jobTitleName, t(`enum_${jobDetails?.contractType}`))?.shareText;
    let jobShareLink = jobDetails?.careerWebsiteShareLink || jobDetails?.shareJobUrl || 'https://sabbar.com/ar/jobs';
    jobShareLink = `${jobShareLink?.split('?')?.[0]}?utm_source=pwa&utm_medium=share_job_modal&utm_campaign=jobseeker_job_post_sharing`;

    const encodedText = encodeURIComponent(`${shareText} ${jobShareLink?.replace('share_job_modal', platform)}`);

    if (platform === 'linkedin') {
        const linkedInJobLink = jobShareLink?.replace('share_job_modal', platform);
        // LinkedIn's share-offsite endpoint only supports url parameter and doesn't show link in comment text
        // To include the link in the comment, we need to use a workaround
        // Try using LinkedIn's feed sharing endpoint which may support text with URL
        // Format: https://www.linkedin.com/feed/?shareActive=true&text=<text>&url=<url>
        const shareTextWithLink = `${shareText}\n\n${linkedInJobLink}`;
        // Use feed sharing endpoint which allows both text and URL
        // This should show the text (including the link) in the comment field AND create a preview card
        url = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareTextWithLink)}&url=${encodeURIComponent(linkedInJobLink)}`;

        // Fallback: If feed sharing doesn't work, use share-offsite (creates preview only, no text)
        // url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(linkedInJobLink)}`;
    } else if (platform === 'twitter') {
        url = `https://twitter.com/intent/tweet?text=${encodedText}`;
    } else if (platform === 'whatsapp') {
        url = `https://wa.me/?text=${encodedText}`;
    } else if (platform === 'instagram') {
        url = `https://instagram.com/share?url=${encodeURIComponent(jobShareLink?.replace('share_job_modal', platform))}`;
    } else if (platform === 'copy') {
        url = jobShareLink;
    }

    return url;
}

export const getAllowedWorkplaceTypes = (workplaceType: JobPostWorkplaceTypeEnum) => {
    if (workplaceType === JobPostWorkplaceTypeEnum.ON_SITE) {
        return [JobPostWorkplaceTypeEnum.ON_SITE];
    } else if (workplaceType === JobPostWorkplaceTypeEnum.FIELD) {
        return [JobPostWorkplaceTypeEnum.FIELD];
    } else if (workplaceType === JobPostWorkplaceTypeEnum.REMOTE) {
        return [JobPostWorkplaceTypeEnum.REMOTE];
    }
    return allWorkplaceTypes;
}

export const MANAGE_LOCATIONS_PHYSICAL_WORKPLACE_TYPES = [JobPostWorkplaceTypeEnum.ON_SITE];
export const MANAGE_LOCATIONS_REMOTE_WORKPLACE_TYPES = [JobPostWorkplaceTypeEnum.FIELD, JobPostWorkplaceTypeEnum.REMOTE];

export const parseWorkplaceTypeParam = (value?: string | null): JobPostWorkplaceTypeEnum | undefined => {
    const normalized = value?.trim().toUpperCase();
    if (
        normalized === JobPostWorkplaceTypeEnum.ON_SITE ||
        normalized === JobPostWorkplaceTypeEnum.REMOTE ||
        normalized === JobPostWorkplaceTypeEnum.FIELD
    ) {
        return normalized;
    }
    return undefined;
}

/** Typesense geo filter matching a job/settings workplace type (district vs city vs country). */
export const getLocationSearchFilter = (workplaceType?: JobPostWorkplaceTypeEnum, isOnsiteFallback?: boolean) => {
    if (workplaceType === JobPostWorkplaceTypeEnum.ON_SITE || (!workplaceType && isOnsiteFallback)) {
        return 'cityOnly:false&&countryOnly:false';
    }
    if (workplaceType === JobPostWorkplaceTypeEnum.FIELD) {
        return 'cityOnly:true&&countryOnly:false';
    }
    return 'cityOnly:true||countryOnly:true';
}

export const getApplicantsColumnId = (applicantStatus: ApplicationStatusEnum, invitedForVetting: boolean) => {
    if (applicantStatus === ApplicationStatusEnum.PENDING && !invitedForVetting) {
        return ApplicantsDragAndDropColumnIdEnum.APPLIED;
    } else if (applicantStatus === ApplicationStatusEnum.PENDING && invitedForVetting) {
        return ApplicantsDragAndDropColumnIdEnum.INTERVIEWED;
    } else if (applicantStatus === ApplicationStatusEnum.SHORT_LISTED) {
        return ApplicantsDragAndDropColumnIdEnum.SHORTLISTED;
    } else if (applicantStatus === ApplicationStatusEnum.FINALIST) {
        return ApplicantsDragAndDropColumnIdEnum.FINALIST;
    } else if (applicantStatus === ApplicationStatusEnum.NOT_SELECTED) {
        return null;
    }

    return null;
}

/**
 * Prepares status data for updateSingleApplicantStatus or updateMultipleApplicantsStatus
 * If statusDef.enum is CUSTOM, sets status to CUSTOM and newStageExternalId to statusDef.value
 * Otherwise, sets status to statusDef.enum
 */
export const prepareStatusForUpdate = (statusDef: { enum: ApplicationStatusEnum; value: string }): { status: ApplicationStatusEnum | string; newStageExternalId?: string } => {
    if (statusDef.enum === ApplicationStatusEnum.CUSTOM) {
        return {
            status: ApplicationStatusEnum.CUSTOM,
            newStageExternalId: statusDef.value
        };
    }
    return {
        status: statusDef.enum
    };
};

export const getFiltersCount = (filters: ApplicantsFilter) => {
    let count = 0;
    const filtersTemp = { ...filters };
    delete filtersTemp.statuses;
    delete filtersTemp.limit;
    delete filtersTemp.page;
    delete filtersTemp.skip;
    delete filtersTemp.sortBy;
    delete filtersTemp.unifiedList;
    delete filtersTemp.returnRecommendations;

    for (const [key, value] of Object.entries(filtersTemp)) {
        if (value) {
            count++;
        }
    }
    return count;
}

// TODO revisit this, probably not needed anymore
export const getApplicantsCountByStatuses = (filterStatuses: ApplicationStatusEnum[], totalCount: number, countApplied: number, countRejected: number, countShortlist: number, countFinalist: number, countVetted: number, isRejectedTabActive: boolean, vettedBySaud: boolean) => {
    const totalCountWithoutRejected = totalCount - countRejected;

    if (isRejectedTabActive) {
        return countRejected;
    }

    if (filterStatuses?.length > 0) {
        // Generate all possible combinations for SHORT_LISTED, FINALIST, PENDING
        const statusSet = new Set(filterStatuses);

        const isPending = statusSet.has(ApplicationStatusEnum.PENDING);
        const isShortlisted = statusSet.has(ApplicationStatusEnum.SHORT_LISTED);
        const isFinalist = statusSet.has(ApplicationStatusEnum.FINALIST);
        const isInterviewed = vettedBySaud;

        // 4 combinations
        if (isShortlisted && isFinalist && isPending) {
            // All
            return totalCountWithoutRejected; // always return this?
        } else if (isShortlisted && isFinalist && isPending) {
            return countShortlist + countFinalist + countApplied;
        } else if (isShortlisted && isFinalist && isInterviewed) {
            return countShortlist + countFinalist + countVetted;
        } else if (isShortlisted && isPending && isInterviewed) {
            return countShortlist + countVetted + countApplied;
        } else if (isFinalist && isPending && isInterviewed) {
            return countFinalist + countVetted + countApplied;
        }
        // 3 combinations
        if (isShortlisted && isFinalist) {
            return countShortlist + countFinalist;
        } else if (isShortlisted && isPending) {
            return countShortlist + countApplied;
        } else if (isShortlisted && isInterviewed) {
            return countShortlist + countVetted;
        } else if (isFinalist && isPending) {
            return countFinalist + countApplied;
        } else if (isFinalist && isInterviewed) {
            return countFinalist + countVetted;
        } else if (isPending && isInterviewed) {
            return countVetted + countApplied;
        }
        // 2 combinations
        if (isShortlisted) {
            return countShortlist;
        }
        if (isFinalist) {
            return countFinalist;
        }
        if (isPending) {
            return countApplied;
        }
        if (isInterviewed) {
            return countVetted;
        }

    }

    return totalCountWithoutRejected;
}




export const getWhatsappSendMessageState = (canSendText: boolean, remainingHoursToSendTxt: number, taskType: ConversationTask, status: ConversationStatus): WhatsappSendMessageState => {

    if (status === ConversationStatus.IN_PROGRESS) {
        return WhatsappSendMessageState.HAS_PENDING_TASK;
    }

    if (canSendText) {
        return WhatsappSendMessageState.CAN_SEND_TEMPLATE_AND_TEXT;
    }


    if (remainingHoursToSendTxt > 24 && !canSendText) {
        return WhatsappSendMessageState.EXPIRED;
    }

    if (remainingHoursToSendTxt > 0 && remainingHoursToSendTxt < 24 && !canSendText) {
        return WhatsappSendMessageState.EXPIRING_SOON_WITH_HOURS_LEFT;
    }

    return WhatsappSendMessageState.CAN_SEND_TEMPLATE_ONLY;
}

export const getApplicantText = (lang: string, count: number) => {
    if (lang === 'en') {
        if (count === 1) {
            return "1 applicant";
        }
        return `${count} applicants`;
    }

    if (count === 1) {
        return "متقدم واحد";
    }
    if (count === 2) {
        return "متقدمين اثنين";
    }
    if (count >= 3 && count <= 10) {
        return `${count} متقدمين`;
    }
    if (count >= 11 && count <= 99) {
        return `${count} متقدما`;
    }
    if (count >= 100) {
        return `${count} متقدمين`;
    }
    // Default fallback if needed
    return `${count} متقدمين`;
}

export const getWorkerNameFromConversation = (conversation: ConversationListItemDto) => {
    if (conversation.worker?.firstName && conversation.worker?.firstName !== 'Unknown' && !conversation?.worker?.isUnknown) {
        return `${conversation.worker.firstName || ''} ${conversation.worker.lastName || ''}`;
    }
    let mobileNumber = conversation.whatsappPhone ? conversation.whatsappPhone : conversation.worker?.phoneNumber || '';
    if (mobileNumber) {
        mobileNumber = '+' + mobileNumber;
    }
    return formatMobileNumber(mobileNumber);
}

export const formatMobileNumber = (mobileNumber: string) => {
    if (!mobileNumber) {
        return '';
    }
    // Remove all non-digit characters except '+' at the beginning
    let sanitized = mobileNumber.trim().replace(/^\+/, '').replace(/\D/g, '');
    // Ensure plus sign
    let withPlus = `+${sanitized}`;
    // Now format as +966 55 550 5055
    // Should be (country) (xx) (xxx) (xxxx)
    // For Saudi numbers with country code 966, typical structure: +966 55 550 5055
    // First 3: country code, next 2: provider, next 3/4: split as in sample

    if (withPlus.startsWith('+966') && withPlus.length === 14) {
        // +966 55 550 5055
        return `${withPlus.slice(0, 4)} ${withPlus.slice(4, 6)} ${withPlus.slice(6, 9)} ${withPlus.slice(9, 13)}`;
    }
    // Fallback: Try to insert spaces appropriately (group by 3 or 4 digits after code)
    return withPlus.replace(/(\+\d{3})(\d{2})(\d{3})(\d{4})/, '$1 $2 $3 $4');
}

/**
 * Ensures a URL has a protocol (https://) if it doesn't already have one
 * @param url - The URL to ensure has a protocol
 * @returns The URL with https:// prefix if needed, or empty string if url is null/undefined
 */
export const ensureUrlProtocol = (url: string | null | undefined): string => {
    if (!url) return '';
    const trimmedUrl = url.trim();
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
        return trimmedUrl;
    }
    return `https://${trimmedUrl}`;
}

/**
 * Extracts first name and last name from a full name string
 * Handles Arabic names where middle name might be father's name:
 * - First name: first word
 * - Last name: if 3+ words, takes 3rd word (skipping middle), otherwise takes 2nd word
 * @param fullName - The full name string to split
 * @returns Object with firstName and lastName properties
 */
export const extractNameFromFullName = (fullName: string | null | undefined): { firstName: string; lastName: string } => {
    if (!fullName) {
        return { firstName: '', lastName: '' };
    }

    const nameParts = fullName.trim().split(' ').filter(part => part.length > 0);

    if (nameParts.length === 0) {
        return { firstName: '', lastName: '' };
    }

    const firstName = nameParts[0] || '';
    // If more than 2 words, take the 3rd word (index 2), otherwise take the 2nd word (index 1)
    const lastName = nameParts.length > 2 ? nameParts[2] : (nameParts[1] || '');

    return { firstName, lastName };
}

export const fixHrInterviewStageName = (stageName: string, lang: string) => {
    if (stageName === 'HR Interview' && lang === 'ar') {
        return 'المقابلة الشخصية';
    }
    if (stageName === 'HR Interview' && lang === 'en') {
        return 'HR Interview';
    }
    if (stageName === 'المقابلة الشخصية' && lang === 'en') {
        return 'HR Interview';
    }
    if (stageName === 'المقابلة الشخصية' && lang === 'ar') {
        return 'المقابلة الشخصية';
    }
    return stageName;
}